/**
 * Grafico de linha da Curva de Mercado (Task #68).
 *
 * 1 linha temporal de preco efetivo R$/MWh por janela, recalculada via 2
 * grupos de pills (radio behavior, 1 selecionado por vez):
 *   - Submercado: SE | S | NE | N (default SE)
 *   - Tipo:       Convencional | I0 | I5 | I100 (default Convencional)
 *
 * Calculo de preco efetivo:
 *   base       = forward_se + agio_se
 *   subm_swap  = (submercado === "SE") ? 0 : swap_<submercado>
 *   tipo_swap  = (tipo === "Convencional") ? 0 : swap_<tipo>
 *   preco_eff  = base + subm_swap + tipo_swap
 *
 * Faixas horizontais PLDmin/PLDmax via ReferenceLine (hardcoded; #65 vai
 * parametrizar depois).
 */
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CurvaForwardRow } from "../../api/types";

interface Props {
  data: CurvaForwardRow[];
}

type Submercado = "SE" | "S" | "NE" | "N";
type Tipo = "Convencional" | "I0" | "I5" | "I100";

const SUBMERCADOS: Submercado[] = ["SE", "S", "NE", "N"];
const TIPOS: Tipo[] = ["Convencional", "I0", "I5", "I100"];

// Limites PLD CCEE 2026 — hardcoded; #65 parametriza via tabela ccee_pld.
const PLD_MIN = 57.31;
const PLD_MAX = 785.27;

const SUBMERCADO_FIELD: Record<Exclude<Submercado, "SE">, keyof CurvaForwardRow> = {
  S: "swap_s",
  NE: "swap_ne",
  N: "swap_n",
};

const TIPO_FIELD: Record<Exclude<Tipo, "Convencional">, keyof CurvaForwardRow> = {
  I0: "swap_i0",
  I5: "swap_i5",
  I100: "swap_i1", // I100% mapeia pro campo swap_i1 (1 = "100%" no payload Zeus)
};

function toN(v: string | null | undefined): number {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function periodoLabel(iso: string): string {
  // YYYY-MM-DD -> "abr/26"
  const [y, m] = iso.split("-");
  const mesesCurto = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const idx = parseInt(m, 10) - 1;
  return `${mesesCurto[idx] ?? m}/${y.slice(2)}`;
}

interface ChartPoint {
  periodo: string;
  preco: number;
  base: number;
  subm_swap: number;
  tipo_swap: number;
  subm_label: Submercado;
  tipo_label: Tipo;
}

function calcularPontos(
  data: CurvaForwardRow[],
  submercado: Submercado,
  tipo: Tipo,
): ChartPoint[] {
  return data.map((row) => {
    const base = toN(row.forward_se) + toN(row.agio_se);
    const subm_swap =
      submercado === "SE" ? 0 : toN(row[SUBMERCADO_FIELD[submercado]] as string | null);
    const tipo_swap =
      tipo === "Convencional" ? 0 : toN(row[TIPO_FIELD[tipo]] as string | null);
    return {
      periodo: periodoLabel(row.dt_inicio),
      preco: Number((base + subm_swap + tipo_swap).toFixed(2)),
      base: Number(base.toFixed(2)),
      subm_swap: Number(subm_swap.toFixed(2)),
      tipo_swap: Number(tipo_swap.toFixed(2)),
      subm_label: submercado,
      tipo_label: tipo,
    };
  });
}

interface PillProps<T extends string> {
  options: readonly T[];
  selected: T;
  onSelect: (v: T) => void;
  testid: string;
}

function PillGroup<T extends string>({ options, selected, onSelect, testid }: PillProps<T>) {
  return (
    <div className="inline-flex flex-wrap gap-1" data-testid={testid}>
      {options.map((opt) => {
        const active = opt === selected;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            data-testid={`${testid}-${opt}`}
            data-active={active}
            className={
              "px-3 py-1 rounded-full text-xs font-medium transition-colors border " +
              (active
                ? "bg-indra-turquesa/20 border-indra-turquesa text-indra-turquesa"
                : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white/80")
            }
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

interface TooltipPayload {
  payload: ChartPoint;
}

function ChartTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  const fmt = (n: number) =>
    n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sign = (n: number) => (n >= 0 ? `+${fmt(n)}` : fmt(n));
  return (
    <div
      className="bg-indra-azul-deep border border-white/10 rounded-md p-3 text-xs text-white/90"
      data-testid="curva-chart-tooltip"
    >
      <div className="font-semibold text-white mb-1">{p.periodo}</div>
      <div className="text-white/60">
        {p.subm_label} {p.tipo_label}
      </div>
      <div className="mt-2 font-mono space-y-0.5">
        <div>base SE = {fmt(p.base)}</div>
        {p.subm_swap !== 0 && <div>swap {p.subm_label} = {sign(p.subm_swap)}</div>}
        {p.tipo_swap !== 0 && <div>swap {p.tipo_label} = {sign(p.tipo_swap)}</div>}
        <div className="border-t border-white/10 mt-1 pt-1 text-indra-turquesa font-semibold">
          R$ {fmt(p.preco)}/MWh
        </div>
      </div>
    </div>
  );
}

export function CurvaMercadoChart({ data }: Props) {
  const [submercado, setSubmercado] = useState<Submercado>("SE");
  const [tipo, setTipo] = useState<Tipo>("Convencional");

  const pontos = useMemo(
    () => calcularPontos(data, submercado, tipo),
    [data, submercado, tipo],
  );

  return (
    <div
      className="rounded-xl border border-white/10 bg-indra-azul-surface p-4 mb-4"
      data-testid="curva-mercado-chart"
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Submercado</span>
          <PillGroup
            options={SUBMERCADOS}
            selected={submercado}
            onSelect={setSubmercado}
            testid="pill-submercado"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Tipo</span>
          <PillGroup
            options={TIPOS}
            selected={tipo}
            onSelect={setTipo}
            testid="pill-tipo"
          />
        </div>
      </div>

      <div className="h-[300px]" data-testid="curva-chart-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pontos} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
            <XAxis
              dataKey="periodo"
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            />
            <YAxis
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              tickFormatter={(v) => v.toLocaleString("pt-BR")}
              domain={["auto", "auto"]}
            />
            <Tooltip content={<ChartTooltip />} />
            <ReferenceLine
              y={PLD_MIN}
              stroke="#FF6B6B"
              strokeDasharray="4 4"
              label={{
                value: `PLDmin ${PLD_MIN}`,
                position: "insideTopLeft",
                fill: "#FF6B6B",
                fontSize: 10,
              }}
            />
            <ReferenceLine
              y={PLD_MAX}
              stroke="#FF6B6B"
              strokeDasharray="4 4"
              label={{
                value: `PLDmax ${PLD_MAX}`,
                position: "insideTopLeft",
                fill: "#FF6B6B",
                fontSize: 10,
              }}
            />
            <Line
              type="monotone"
              dataKey="preco"
              stroke="#00DAC8"
              strokeWidth={2}
              dot={{ r: 3, fill: "#00DAC8" }}
              isAnimationActive={false}
              name="Preço efetivo"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
